import React, { useState, useEffect } from 'react';
import { Download, Calendar } from 'lucide-react';
import api from '../../services/api';
import ReportFilters, { getDefaultDateRange } from './ReportFilters';

const MonthlyOverviewReport = () => {
  const [data, setData] = useState({ results: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    ...getDefaultDateRange(),
    supplier: '',
    cost_centre: '',
    paid_status: '',
  });

  const fetchData = async (appliedFilters = filters) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (appliedFilters.date_from) params.append('date_from', appliedFilters.date_from);
      if (appliedFilters.date_to) params.append('date_to', appliedFilters.date_to);
      if (appliedFilters.supplier) params.append('supplier', appliedFilters.supplier);
      if (appliedFilters.cost_centre) params.append('cost_centre', appliedFilters.cost_centre);
      if (appliedFilters.paid_status) params.append('paid_status', appliedFilters.paid_status);
      
      const response = await api.get(`/api/reports/monthly-overview/?${params.toString()}`);
      const responseData = response.data;
      setData({
        results: Array.isArray(responseData) ? responseData : (responseData.results || []),
        totals: responseData.totals || {}
      });
    } catch (err) {
      setError('Failed to load report data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchData(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      ...getDefaultDateRange(),
      supplier: '',
      cost_centre: '',
      paid_status: '',
    };
    setFilters(defaultFilters);
    fetchData(defaultFilters);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.supplier) params.append('supplier', filters.supplier);
      if (filters.cost_centre) params.append('cost_centre', filters.cost_centre);
      if (filters.paid_status) params.append('paid_status', filters.paid_status);
      
      const response = await api.get(`/api/reports/monthly-overview/export/?${params.toString()}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monthly_overview_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report');
    }
  };

  const formatCurrency = (amount) => {
    return `R ${parseFloat(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  // Calculate paid percentage for progress bar
  const getPaidPercentage = (paid, total) => {
    if (!total || total === 0) return 0;
    return Math.round((paid / total) * 100);
  };

  return (
    <div className="report-container">
      <div className="report-header">
        <h1>Monthly Overview Report</h1>
        <button className="btn btn-primary" onClick={handleExport} disabled={loading}>
          <Download size={18} />
          Export to Excel
        </button>
      </div>

      <div className="card">
        <ReportFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          showPaidStatus={false}  // Monthly overview shows both anyway
        />
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        {/* Summary Stats */}
        <div className="report-stats">
          <div className="stat-card">
            <div className="stat-label">Total Amount</div>
            <div className="stat-value">{formatCurrency(data.totals?.grand_total)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Payments</div>
            <div className="stat-value">{data.totals?.total_payments || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Paid Amount</div>
            <div className="stat-value success">{formatCurrency(data.totals?.total_paid_amount)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Outstanding</div>
            <div className="stat-value warning">{formatCurrency(data.totals?.total_unpaid_amount)}</div>
          </div>
        </div>

        {/* Data Table */}
        {loading ? (
          <div className="loading">Loading report data...</div>
        ) : data.results.length === 0 ? (
          <div className="empty-state">No data found for the selected filters</div>
        ) : (
          <div className="table-responsive">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="text-right">Total Amount</th>
                  <th className="text-center">Payments</th>
                  <th className="text-right">Paid Amount</th>
                  <th className="text-right">Unpaid Amount</th>
                  <th className="text-center">Batches</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <div className="month-cell">
                        <Calendar size={16} className="month-icon" />
                        <span>{row.month_display}</span>
                      </div>
                    </td>
                    <td className="text-right"><strong>{formatCurrency(row.total_amount)}</strong></td>
                    <td className="text-center">{row.payment_count}</td>
                    <td className="text-right text-success">{formatCurrency(row.paid_amount)}</td>
                    <td className="text-right text-warning">{formatCurrency(row.unpaid_amount)}</td>
                    <td className="text-center">{row.batch_count}</td>
                    <td>
                      <div className="progress-cell">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill"
                            style={{ width: `${getPaidPercentage(row.paid_count, row.payment_count)}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">
                          {row.paid_count}/{row.payment_count} paid
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total</strong></td>
                  <td className="text-right"><strong>{formatCurrency(data.totals?.grand_total)}</strong></td>
                  <td className="text-center"><strong>{data.totals?.total_payments}</strong></td>
                  <td className="text-right"><strong>{formatCurrency(data.totals?.total_paid_amount)}</strong></td>
                  <td className="text-right"><strong>{formatCurrency(data.totals?.total_unpaid_amount)}</strong></td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyOverviewReport;